import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useLocation } from 'react-router-dom';

import { PostBannerDecorator } from '../../components/PostBannerDecorator';
import { IParseJournalBannersItem, parseJournalBanners } from './parseJournalBanners';
import { parseDateRanges } from '../parseDateRanges';
import { isDateIncludedInDateRanges } from '../isDateIncludedInDateRanges';
import { useEventTracking } from '../useEventTracking';

interface IUseContentParams {
  canParse: boolean;
  canRender: boolean;
  postContent: string;
  setPostContent: Dispatch<SetStateAction<string>>;
  done(): void;
  isPostPreview?: boolean;
  options?: { isEnabled: boolean };
}

/**
 * Рендерит и управляет баннерами в посте
 */
export const useContentJournalBannersNew = ({
  canParse,
  canRender,
  postContent,
  setPostContent,
  done,
  isPostPreview,
  options,
}: IUseContentParams) => {
  const { pathname } = useLocation();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const [isParsed, setIsParsed] = useState(false);
  const [parsedParams, setParsedParams] = useState<IParseJournalBannersItem[]>([]);
  const { trackEvent } = useEventTracking();

  /** Сбрасывает стэйт при клиентском переходе с поста на пост */
  useEffect(() => {
    if (pathname !== prevPathname) {
      setPrevPathname(pathname);
      setIsParsed(false);
      setParsedParams([]);
    }
  }, [pathname, prevPathname]);

  /** Трекает клик по баннеру */
  const handleClick = useCallback(
    (url: string, position: IParseJournalBannersItem['position']) => {
      if (isPostPreview) {
        return;
      }

      trackEvent({
        category: 'Magazine',
        action: `click_${position}_banner`,
        label: url,
      });
    },
    [isPostPreview, trackEvent],
  );

  /** Рендерит баннеры */
  useEffect(() => {
    if (!parsedParams.length || !canRender) {
      return;
    }

    parsedParams.forEach(banner => {
      const root = document.getElementById(banner.id);

      if (root) {
        createRoot(root).render(
          <PostBannerDecorator onClick={url => handleClick(url, banner.position)}>
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: banner.outerHtml }} />
          </PostBannerDecorator>,
        );
      }
    });
  }, [canRender, handleClick, parsedParams]);

  /** Парсит параметры баннеров */
  useEffect(() => {
    if (!canParse || isParsed) {
      return;
    }

    const { items, html } = parseJournalBanners(postContent, ({ id, dateRanges }) => {
      /** Если баннеры отключены для этой статьи, то вырезаем баннер */
      if (options?.isEnabled === false) {
        return '';
      }

      const parsedDateRanges = parseDateRanges(dateRanges || '');

      /**
       * Если заданы периоды показа и ни один не актуален для текущей даты,
       * то вырезам баннер
       */
      if (parsedDateRanges?.length && !isDateIncludedInDateRanges(parsedDateRanges)) {
        return '';
      }

      return `<div id="${id}"></div>`;
    });

    if (items.length) {
      setPostContent(html);
      setParsedParams(items);
    }

    setIsParsed(true);
    done();
  }, [canParse, done, isParsed, options?.isEnabled, postContent, setPostContent]);
};
